import * as React from "react";

import cn from "classnames";

import styles from "./TextField.module.scss";

interface FieldWrapperProps {
    /**
     * Any errors that should be displayed below the field.
     */
    fieldErrors?: string[];
    label?: string;
    id?: string;
    className?: string;
    inputClassName?: string;
    outlineClassName?: string;
    errorClassName?: string;
    labelClassName?: string;
}

interface BaseFieldProps<T extends HTMLElement> extends FieldWrapperProps, Partial<React.HTMLProps<T>> {}

/**
 * Generates a text field, including label and errors.
 */
const TextField = (props: BaseFieldProps<HTMLInputElement>): React.ReactElement => {
    const {
        id,
        label,
        autoComplete = "none",
        className,
        inputClassName,
        outlineClassName,
        labelClassName,
        errorClassName,
        fieldErrors = [],
        required = false,
        placeholder = "",
        ...inputProps
    } = props;

    const hasErrors = fieldErrors.length > 0;

    const inputGroupClassNames = cn(
        styles.inputGroup,
        hasErrors ? cn(styles.groupError, errorClassName) : "",
    );

    const labelClassNames = cn(styles.label, labelClassName, hasErrors ? styles.labelError : "");

    return (
        <span className={cn(styles.field, className)}>
            {label ? (
                <label htmlFor={id} className={labelClassNames}>
                {label}{required ? "*" : ""}
                </label>
            ) : null}
            <span className={inputGroupClassNames}>
                <input
                    className={cn(styles.input, inputClassName)}
                    id={id}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    type="text"
                    autoComplete={autoComplete}
                    aria-required={required}
                    required={required}
                    placeholder={placeholder}
                    aria-label={hasErrors ? fieldErrors.join(" ") : label}
                    {...inputProps}
                />
                <span className={cn(styles.outline, outlineClassName)}></span>
            </span>
            <span className={styles.error}>
                {fieldErrors.map((error) => (
                    <span key={error} className={cn(styles.error, styles.fieldError)}>
                        Error: {error}
                    </span>
                ))}
            </span>
        </span>
    );
}

export default TextField;
export type { BaseFieldProps, FieldWrapperProps };
